import React, { useState, Fragment, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDynamicPermissions } from '../../hooks/useDynamicPermissions';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { ArrowLeftIcon, CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { Combobox, Transition } from '@headlessui/react';
import { useAuth } from '../../contexts/AuthContext';
import { useIsServiceDeskUser } from '../../hooks/useIsServiceDeskUser';
import { toast } from 'react-hot-toast';
import RichTextEditor from '../../components/RichTextEditor';
import { trackServiceRequestOperation } from '../../utils/appInsights';
import { useEngagementResources } from '../../hooks/useEngagementResources';
import { fetchAssignmentGroupMembers } from '../../utils/groupMembers';

interface FormData {
  shortDescription: string;
  description: string;
  customerId: string;
  engagementId: string;
  servicePlanId: string;
  resourceId: string;
  otherResourceNames: string;
  impact: string;
  urgency: string;
  assignmentGroupId: string;
  assignedTo: string;
  state: string;
  contactId: string;
}

const ServiceRequestCreate: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can, loading: permissionsLoading } = useDynamicPermissions();
  const hasPermission = can('service_requests', 'create');



  // Priority calculation function (matches backend logic)
  const calculatePriorityFromImpactUrgency = (impact: string, urgency: string): string => {
    if (!impact || !urgency) return 'low_4'; // Default to Low

    const impactNum = parseInt(impact.split('_')[1]);
    const urgencyNum = parseInt(urgency.split('_')[1]);

    if (impactNum === 1 && urgencyNum === 1) return 'critical_1';
    if ((impactNum === 1 && urgencyNum === 2) || (impactNum === 2 && urgencyNum === 1)) return 'high_2';
    if ((impactNum === 1 && urgencyNum === 3) || (impactNum === 2 && urgencyNum === 2) || (impactNum === 3 && urgencyNum === 1)) return 'moderate_3';
    if ((impactNum === 2 && urgencyNum === 3) || (impactNum === 3 && urgencyNum === 2)) return 'low_4';
    if (impactNum === 3 && urgencyNum === 3) return 'planning_5';

    return 'low_4'; // Default to Low
  };
  const { user } = useAuth();
  const isServiceDeskUser = useIsServiceDeskUser();
  const [formData, setFormData] = useState<FormData>({
    shortDescription: '',
    description: '',
    customerId: '',
    engagementId: '',
    servicePlanId: '',
    resourceId: '',
    impact: 'low_3',
    urgency: 'low_3',
    assignmentGroupId: '',
    assignedTo: '',
    state: 'new',
    contactId: '',
    otherResourceNames: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedResourceIdValue, setSelectedResourceIdValue] = useState<string>('');
  const [isOtherResource, setIsOtherResource] = useState<boolean>(false);
  const [pinnedResource, setPinnedResource] = useState<{
    id: string;
    name: string;
    resourceId?: string;
  } | null>(null);
  const [contactQuery, setContactQuery] = useState('');
  const [customerQuery, setCustomerQuery] = useState('');
  const [engagementQuery, setEngagementQuery] = useState('');
  const [resourceQuery, setResourceQuery] = useState('');
  const [assignmentGroupQuery, setAssignmentGroupQuery] = useState('');
  const [assignedToQuery, setAssignedToQuery] = useState('');

  // Fetch group members for assignment dropdown - only when assignment group is selected
  const { data: groupMembersData } = useQuery({
    queryKey: ['group-members', formData.assignmentGroupId, formData.customerId],
    queryFn: async () => {
      if (!formData.assignmentGroupId) return [];
      try {
        return await fetchAssignmentGroupMembers(formData.assignmentGroupId, formData.customerId || null);
      } catch (error) {
        console.error('Error fetching group members:', error);
        return [];
      }
    },
    enabled: !!formData.assignmentGroupId
  });

  // Fetch assignment groups for dropdown (only groups with type 'assignment')
  const { data: assignmentGroupsData } = useQuery({
    queryKey: ['assignment-groups'],
    queryFn: async () => {
      const response = await api.get('/groups/assignment-groups');
      return response.data;
    }
  });

  // Fetch customers for dropdown
  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const response = await api.get('/customers');
      return response.data;
    }
  });

  // Fetch engagements for dropdown - only when customer is selected
  const { data: engagementsData } = useQuery({
    queryKey: ['engagements', formData.customerId],
    queryFn: async () => {
      if (!formData.customerId) return { engagements: [] };
      const response = await api.get(`/engagements?customerId=${formData.customerId}`);
      return response.data;
    },
    enabled: !!formData.customerId
  });

  // Fetch service plans for dropdown - fetch all available plans
  const { data: servicePlansData } = useQuery({
    queryKey: ['service-plans'],
    queryFn: async () => {
      const response = await api.get('/service-plans');
      return response.data;
    }
  });

  // Fetch resources for dropdown - ServiceNow CIs when engagement is SN-configured
  const { data: engagementResources } = useEngagementResources(
    formData.engagementId,
    resourceQuery,
    pinnedResource
  );
  const resourcesData = engagementResources?.resources || [];
  const isServiceNowResourceSource = engagementResources?.source === 'servicenow';

  // Fetch contacts for Contact dropdown - filtered by customer
  const { data: contactsData } = useQuery({
    queryKey: ['contacts', formData.customerId, contactQuery],
    queryFn: async () => {
      if (!formData.customerId) return { users: [] };
      const params = new URLSearchParams({
        class: 'contact',
        customerId: formData.customerId,
        limit: '100'
      });
      if (contactQuery) {
        params.append('search', contactQuery);
      }
      const response = await api.get(`/users?${params.toString()}`);
      return response.data;
    },
    enabled: !!formData.customerId
  });

  // Fetch primary contact for auto-fill when customer/engagement is selected
  const { data: primaryContactData } = useQuery({
    queryKey: ['primary-contact', formData.customerId, formData.engagementId],
    queryFn: async () => {
      if (!formData.customerId) return { contact: null };
      const params = new URLSearchParams({ customerId: formData.customerId });
      if (formData.engagementId) {
        params.append('engagementId', formData.engagementId);
      }
      const response = await api.get(`/contacts/primary?${params.toString()}`);
      return response.data;
    },
    enabled: !!formData.customerId
  });

  // Auto-fill contact when customer/engagement is selected and primary contact is found
  useEffect(() => {
    if (formData.customerId && primaryContactData?.contact && !formData.contactId) {
      setFormData(prev => ({
        ...prev,
        contactId: primaryContactData.contact.id
      }));
    }
  }, [formData.customerId, formData.engagementId, primaryContactData, formData.contactId]);

  const createServiceRequestMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/cases', data);
      return response.data;
    },
    onSuccess: async (data) => {
      // Invalidate list queries so they refetch when user navigates back
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['service-requests'] }),
        queryClient.invalidateQueries({ queryKey: ['cases'] })
      ]);
      toast.success('Service request created successfully');
      
      // Log service request creation
      if (user) {
        const serviceRequestNumber = data?.number || data?.case?.number || data?.id || data?._id || 'Unknown';
        trackServiceRequestOperation(
          'Service Request Created',
          user.email || user.firstName || 'Unknown',
          {
            action: 'Service Request Created',
            serviceRequestNumber: serviceRequestNumber,
          },
          {
            firstName: user.firstName,
            lastName: user.lastName,
            role: user.role,
            roleType: user.roleType,
            roleLevel: user.roleLevel,
            userClass: user.class,
            customerId: user.customerId,
            customerName: user.customerName,
            engagementId: user.engagementId,
            engagementName: user.engagementName,
            isServiceDeskUser: isServiceDeskUser,
          }
        );
      }
      
      navigate(`/service-requests/${data.id || data._id}`);
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to create service request';
      setErrors({ submit: message });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const newErrors: Record<string, string> = {};
    if (!formData.shortDescription.trim()) {
      newErrors.shortDescription = 'Short Description is required';
    }
    if (!formData.impact) {
      newErrors.impact = 'Impact is required';
    }
    if (!formData.urgency) {
      newErrors.urgency = 'Urgency is required';
    }
    if (!formData.customerId) {
      newErrors.customerId = 'Customer is required';
    }
    if (!formData.engagementId) {
      newErrors.engagementId = 'Engagement is required';
    }
    if (!formData.resourceId && !isOtherResource) {
      newErrors.resourceId = formData.engagementId
        ? 'Resource is required (select a resource or choose "Others")'
        : 'Please select an engagement first';
    }
    if (isOtherResource && !formData.otherResourceNames.trim()) {
      newErrors.otherResourceNames = 'Please enter at least one resource name';
    }
    if (!formData.assignmentGroupId) {
      newErrors.assignmentGroupId = 'Assignment Group is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Clean up empty fields and calculate priority
    const calculatedPriority = calculatePriorityFromImpactUrgency(formData.impact, formData.urgency);
    const submitData: any = {
      shortDescription: formData.shortDescription,
      channel: 'web', // Default channel for service requests created through web interface
      category: 'request', // Default category
      impact: formData.impact,
      urgency: formData.urgency,
      priority: calculatedPriority,
      ticketType: 'SERVICE_REQUEST',
      state: formData.state
    };

    if (formData.customerId) submitData.customerId = formData.customerId;
    if (formData.engagementId) submitData.engagementId = formData.engagementId;
    if (formData.servicePlanId) submitData.servicePlanId = formData.servicePlanId;

    // Add resource - either resourceId OR otherResourceNames (mutually exclusive)
    if (isOtherResource && formData.otherResourceNames.trim()) {
      submitData.otherResourceNames = formData.otherResourceNames.trim();
      submitData.resourceId = null; // Explicitly set to null when using Others
    } else if (formData.resourceId) {
      submitData.resourceId = formData.resourceId;
      submitData.otherResourceNames = null; // Clear otherResourceNames when using resource
    }

    if (formData.assignedTo) submitData.assignedToId = formData.assignedTo;
    if (formData.assignmentGroupId) submitData.assignmentGroupId = formData.assignmentGroupId;
    if (formData.contactId) submitData.contactId = formData.contactId;
    if (formData.description) submitData.description = formData.description;
    if (user?.id) submitData.createdBy = user.id;

    createServiceRequestMutation.mutate(submitData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const fieldValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

    setFormData(prev => {
      const newFormData = {
        ...prev,
        [name]: fieldValue
      };

      // Reset dependent fields when parent fields change
      if (name === 'customerId') {
        newFormData.engagementId = '';
        newFormData.servicePlanId = '';
        newFormData.resourceId = '';
        newFormData.contactId = '';
      }
      if (name === 'engagementId') {
        newFormData.servicePlanId = '';
        newFormData.resourceId = '';
        newFormData.contactId = '';
        newFormData.otherResourceNames = '';
        setIsOtherResource(false);
        setSelectedResourceIdValue('');
        setPinnedResource(null);
      }
      if (name === 'assignmentGroupId') {
        newFormData.assignedTo = '';
      }

      return newFormData;
    });
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };


  const impacts = [
    { value: 'high_1', label: '1 - High' },
    { value: 'medium_2', label: '2 - Medium' },
    { value: 'low_3', label: '3 - Low' }
  ];

  const urgencies = [
    { value: 'high_1', label: '1 - High' },
    { value: 'medium_2', label: '2 - Medium' },
    { value: 'low_3', label: '3 - Low' }
  ];

  // Filtered data for comboboxes
  const filteredCustomers = customerQuery === ''
    ? (customersData?.customers || [])
    : (customersData?.customers || []).filter((c: any) =>
        c.accountName?.toLowerCase().includes(customerQuery.toLowerCase())
      );

  const filteredEngagements = engagementQuery === ''
    ? (engagementsData?.engagements || [])
    : (engagementsData?.engagements || []).filter((e: any) =>
        e.name?.toLowerCase().includes(engagementQuery.toLowerCase())
      );

  const resourcesArray = Array.isArray(resourcesData) ? resourcesData : [];
  const filteredResources =
    isServiceNowResourceSource || resourceQuery === ''
      ? resourcesArray
      : resourcesArray.filter((r: any) =>
          r.name?.toLowerCase().includes(resourceQuery.toLowerCase())
        );

  const filteredAssignmentGroups = (() => {
    const groups = assignmentGroupsData || [];
    if (assignmentGroupQuery === '') {
      return groups;
    }
    return groups.filter((g: any) =>
      g.name?.toLowerCase().includes(assignmentGroupQuery.toLowerCase())
    );
  })();

  const filteredGroupMembers = React.useMemo(() => {
    const members = groupMembersData || [];
    if (assignedToQuery === '') {
      return members;
    }
    return members.filter((u: any) =>
      `${u.firstName} ${u.lastName}`.toLowerCase().includes(assignedToQuery.toLowerCase())
    );
  }, [groupMembersData, assignedToQuery]);

  // Show loading while checking permissions
  if (permissionsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Check permission
  if (!hasPermission) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          You don't have permission to create service requests.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-8 py-6">
        <div className="mb-6">
          <button
            onClick={() => navigate('/service-requests')}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to Service Requests
          </button>
        </div>

        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h1 className="text-xl font-semibold text-gray-900">Create Service Request</h1>
          </div>

          <form onSubmit={handleSubmit} className="p-8">
            {errors.submit && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-800">{errors.submit}</p>
              </div>
            )}

            {/* Row 1: Number and State */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Number
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value=""
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                      State
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value="New"
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 2: Impact and Urgency */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="impact" className="block text-sm font-medium text-gray-700 mb-1">
                      Impact <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <select
                      id="impact"
                      name="impact"
                      value={formData.impact}
                      onChange={handleChange}
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        errors.impact
                          ? 'border-red-300 focus:ring-red-400'
                          : 'border-gray-300'
                      }`}
                    >
                      {impacts.map(impact => (
                        <option key={impact.value} value={impact.value}>{impact.label}</option>
                      ))}
                    </select>
                    {errors.impact && (
                      <p className="mt-1 text-sm text-red-600">{errors.impact}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="urgency" className="block text-sm font-medium text-gray-700 mb-1">
                      Urgency <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <select
                      id="urgency"
                      name="urgency"
                      value={formData.urgency}
                      onChange={handleChange}
                      className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        errors.urgency
                          ? 'border-red-300 focus:ring-red-400'
                          : 'border-gray-300'
                      }`}
                    >
                      {urgencies.map(urgency => (
                        <option key={urgency.value} value={urgency.value}>{urgency.label}</option>
                      ))}
                    </select>
                    {errors.urgency && (
                      <p className="mt-1 text-sm text-red-600">{errors.urgency}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 3: Priority (calculated) */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority
                    </label>
                  </div>
                  <div className="col-span-9">
                    <input
                      type="text"
                      value={(() => {
                        const priority = calculatePriorityFromImpactUrgency(formData.impact, formData.urgency);
                        switch (priority) {
                          case 'critical_1': return '1 - Critical';
                          case 'high_2': return '2 - High';
                          case 'moderate_3': return '3 - Moderate';
                          case 'low_4': return '4 - Low';
                          case 'planning_5': return '5 - Planning';
                          default: return '4 - Low';
                        }
                      })()}
                      disabled
                      readOnly
                      className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 4: Customer and Engagement */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="customerId" className="block text-sm font-medium text-gray-700 mb-1">
                      Customer <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.customerId}
                      onChange={(value) => {
                        setFormData(prev => ({
                          ...prev,
                          customerId: value || '',
                          engagementId: '',
                          servicePlanId: '',
                          resourceId: '',
                          contactId: ''
                        }));
                        setIsOtherResource(false);
                        setSelectedResourceIdValue('');
                        setPinnedResource(null);
                        if (errors.customerId) {
                          setErrors(prev => ({ ...prev, customerId: '' }));
                        }
                      }}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                            errors.customerId
                              ? 'border-red-300 focus:ring-red-400'
                              : 'border-gray-300'
                          }`}
                          displayValue={(customerId: string) => {
                            const customer = (customersData?.customers || []).find((c: any) => c.id === customerId);
                            return customer ? customer.accountName : '';
                          }}
                          onChange={(event) => setCustomerQuery(event.target.value)}
                          placeholder="Search customers..."
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setCustomerQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {filteredCustomers.length === 0 ? (
                              <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                No customers found.
                              </div>
                            ) : (
                              filteredCustomers.map((customer: any) => (
                                <Combobox.Option
                                  key={customer.id}
                                  value={customer.id}
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                      active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ active, selected }) => (
                                    <>
                                      <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                        {customer.accountName}
                                      </span>
                                      {selected && (
                                        <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Combobox.Option>
                              ))
                            )}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                    {errors.customerId && (
                      <p className="mt-1 text-sm text-red-600">{errors.customerId}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="engagementId" className="block text-sm font-medium text-gray-700 mb-1">
                      Engagement <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.engagementId}
                      onChange={(value) => {
                        setFormData(prev => ({
                          ...prev,
                          engagementId: value || '',
                          servicePlanId: '',
                          resourceId: '',
                          contactId: ''
                        }));
                        setIsOtherResource(false);
                        setSelectedResourceIdValue('');
                        setPinnedResource(null);
                        if (errors.engagementId) {
                          setErrors(prev => ({ ...prev, engagementId: '' }));
                        }
                      }}
                      disabled={!formData.customerId}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-gray-50 disabled:text-gray-400 ${
                            errors.engagementId
                              ? 'border-red-300 focus:ring-red-400'
                              : 'border-gray-300'
                          }`}
                          displayValue={(engagementId: string) => {
                            const engagement = (engagementsData?.engagements || []).find((e: any) => e.id === engagementId);
                            return engagement ? engagement.name : '';
                          }}
                          onChange={(event) => setEngagementQuery(event.target.value)}
                          placeholder={!formData.customerId ? 'Select Customer first' : 'Search engagements...'}
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setEngagementQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {filteredEngagements.length === 0 ? (
                              <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                No engagements found.
                              </div>
                            ) : (
                              filteredEngagements.map((engagement: any) => (
                                <Combobox.Option
                                  key={engagement.id}
                                  value={engagement.id}
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                      active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ active, selected }) => (
                                    <>
                                      <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                        {engagement.name}
                                      </span>
                                      {selected && (
                                        <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Combobox.Option>
                              ))
                            )}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                    {errors.engagementId && (
                      <p className="mt-1 text-sm text-red-600">{errors.engagementId}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 5: Contact and Service Plan */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="contactId" className="block text-sm font-medium text-gray-700 mb-1">
                      Contact
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.contactId}
                      onChange={(value) => {
                        setFormData({ ...formData, contactId: value || '' });
                        if (errors.contactId) {
                          setErrors(prev => ({ ...prev, contactId: '' }));
                        }
                      }}
                      disabled={!formData.customerId}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-gray-50 disabled:text-gray-400 ${
                            errors.contactId
                              ? 'border-red-300 focus:ring-red-400'
                              : 'border-gray-300'
                          }`}
                          displayValue={(userId: string) => {
                            const user = (contactsData?.users || []).find((u: any) => u.id === userId);
                            return user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email : '';
                          }}
                          onChange={(event) => setContactQuery(event.target.value)}
                          placeholder={!formData.customerId ? 'Select Customer first' : 'Search contacts...'}
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setContactQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {contactsData?.users && contactsData.users.length === 0 && contactQuery !== '' ? (
                              <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                No contacts found.
                              </div>
                            ) : contactsData?.users && contactsData.users.length === 0 ? (
                              <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                No contacts available for this customer.
                              </div>
                            ) : (
                              (contactsData?.users || []).map((user: any) => (
                                <Combobox.Option
                                  key={user.id}
                                  value={user.id}
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                      active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ active, selected }) => (
                                    <>
                                      <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                        {`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}
                                      </span>
                                      {selected && (
                                        <span
                                          className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                                            active ? 'text-white' : 'text-indigo-600'
                                          }`}
                                        >
                                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Combobox.Option>
                              ))
                            )}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                    {errors.contactId && (
                      <p className="mt-1 text-sm text-red-600">{errors.contactId}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="servicePlanId" className="block text-sm font-medium text-gray-700 mb-1">
                      Service Plan
                    </label>
                  </div>
                  <div className="col-span-9">
                    <select
                      id="servicePlanId"
                      name="servicePlanId"
                      value={formData.servicePlanId}
                      onChange={handleChange}
                      className="w-full px-3 py-1 border border-gray-300 rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    >
                      <option value="">Select Service Plan</option>
                      {servicePlansData?.plans?.map((plan: any) => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Row 6: Resource and Resource ID / Custom Resources */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="resourceId" className="block text-sm font-medium text-gray-700 mb-1">
                      Resource <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={isOtherResource ? '__OTHER__' : formData.resourceId}
                      onChange={(value) => {
                        if (value === '__OTHER__') {
                          setIsOtherResource(true);
                          setPinnedResource(null);
                          setFormData(prev => ({ ...prev, resourceId: '' }));
                          setSelectedResourceIdValue('');
                        } else {
                          setIsOtherResource(false);
                          const selectedResource = resourcesArray.find((r: any) => r.id === value);
                          setPinnedResource(
                            selectedResource
                              ? {
                                  id: selectedResource.id,
                                  name: selectedResource.name,
                                  resourceId: selectedResource.resourceId,
                                }
                              : null
                          );
                          setSelectedResourceIdValue(selectedResource?.resourceId || '');
                          setFormData(prev => ({ ...prev, resourceId: value || '', otherResourceNames: '' }));
                        }
                        if (errors.resourceId) {
                          setErrors(prev => ({ ...prev, resourceId: '' }));
                        }
                        if (errors.otherResourceNames) {
                          setErrors(prev => ({ ...prev, otherResourceNames: '' }));
                        }
                      }}
                      disabled={!formData.engagementId}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-gray-50 disabled:text-gray-400 ${
                            errors.resourceId
                              ? 'border-red-300 focus:ring-red-400'
                              : 'border-gray-300'
                          }`}
                          displayValue={(resourceId: string) => {
                            if (isOtherResource) return 'Others (specify custom resources)';
                            const resource = resourcesArray.find((r: any) => r.id === resourceId);
                            return resource?.name || pinnedResource?.name || '';
                          }}
                          onChange={(event) => setResourceQuery(event.target.value)}
                          placeholder={!formData.engagementId ? 'Select Engagement first' : 'Search resources...'}
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setResourceQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {filteredResources.length === 0 ? (
                              <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                No resources found.
                              </div>
                            ) : (
                              filteredResources.map((resource: any) => (
                                <Combobox.Option
                                  key={resource.id}
                                  value={resource.id}
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                      active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ active, selected }) => (
                                    <>
                                      <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                        {resource.name}
                                      </span>
                                      {selected && (
                                        <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Combobox.Option>
                              ))
                            )}
                            {/* Others option */}
                            <Combobox.Option
                              key="__OTHER__"
                              value="__OTHER__"
                              className={({ active }) =>
                                `relative cursor-pointer select-none py-2 pl-3 pr-9 border-t border-gray-200 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                              }
                            >
                              {({ selected, active }) => (
                                <>
                                  <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                    Others (specify custom resources)
                                  </span>
                                  {selected && (
                                    <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                      <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                    </span>
                                  )}
                                </>
                              )}
                            </Combobox.Option>
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                    {errors.resourceId && (
                      <p className="mt-1 text-sm text-red-600">{errors.resourceId}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className={`grid grid-cols-12 gap-4 ${isOtherResource ? 'items-start' : 'items-end'}`}>
                  <div className="col-span-3">
                    <label className={`block text-sm font-medium text-gray-700 ${isOtherResource ? 'mt-1' : 'mb-1'}`}>
                      {isOtherResource ? 'Custom Resources' : 'Resource ID'}
                      {isOtherResource && <span className="text-red-500"> *</span>}
                    </label>
                  </div>
                  <div className="col-span-9">
                    {isOtherResource ? (
                      <>
                        <input
                          type="text"
                          value={formData.otherResourceNames}
                          onChange={(e) => {
                            setFormData(prev => ({ ...prev, otherResourceNames: e.target.value }));
                            if (errors.otherResourceNames) {
                              setErrors(prev => ({ ...prev, otherResourceNames: '' }));
                            }
                          }}
                          placeholder="Enter resource names, separated by commas (e.g., Server-01, Database-02)"
                          className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${errors.otherResourceNames ? 'border-red-300' : 'border-gray-300'}`}
                        />
                        {errors.otherResourceNames && (
                          <p className="mt-1 text-sm text-red-600">{errors.otherResourceNames}</p>
                        )}
                        <p className="mt-1 text-xs text-gray-500">Enter multiple resource names separated by commas</p>
                      </>
                    ) : (
                      <input
                        type="text"
                        value={selectedResourceIdValue}
                        readOnly
                        disabled
                        className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8"
                        placeholder={formData.resourceId ? '' : 'Select a resource to see ID'}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 7: Assignment Group and Assigned To */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="assignmentGroupId" className="block text-sm font-medium text-gray-700 mb-1">
                      Assignment Group <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.assignmentGroupId}
                      onChange={(value) => {
                        setFormData(prev => ({
                          ...prev,
                          assignmentGroupId: value || '',
                          assignedTo: ''
                        }));
                        if (errors.assignmentGroupId) {
                          setErrors(prev => ({ ...prev, assignmentGroupId: '' }));
                        }
                      }}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                            errors.assignmentGroupId
                              ? 'border-red-300 focus:ring-red-400'
                              : 'border-gray-300'
                          }`}
                          displayValue={(groupId: string) => {
                            const group = (assignmentGroupsData || []).find((g: any) => g.id === groupId);
                            return group ? group.name : '';
                          }}
                          onChange={(event) => setAssignmentGroupQuery(event.target.value)}
                          placeholder="Search assignment groups..."
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setAssignmentGroupQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {filteredAssignmentGroups.length === 0 ? (
                              <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                No groups found.
                              </div>
                            ) : (
                              filteredAssignmentGroups.map((group: any) => (
                                <Combobox.Option
                                  key={group.id}
                                  value={group.id}
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                      active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ active, selected }) => (
                                    <>
                                      <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                        {group.name}
                                      </span>
                                      {selected && (
                                        <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Combobox.Option>
                              ))
                            )}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                    {errors.assignmentGroupId && (
                      <p className="mt-1 text-sm text-red-600">{errors.assignmentGroupId}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-6">
                <div className="grid grid-cols-12 gap-4 items-end">
                  <div className="col-span-3">
                    <label htmlFor="assignedTo" className="block text-sm font-medium text-gray-700 mb-1">
                      Assigned To
                    </label>
                  </div>
                  <div className="col-span-9">
                    <Combobox
                      value={formData.assignedTo}
                      onChange={(value) => {
                        setFormData(prev => ({ ...prev, assignedTo: value || '' }));
                        if (errors.assignedTo) {
                          setErrors(prev => ({ ...prev, assignedTo: '' }));
                        }
                      }}
                      disabled={!formData.assignmentGroupId}
                    >
                      <div className="relative">
                        <Combobox.Input
                          className={`w-full px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-gray-50 disabled:text-gray-400 ${
                            errors.assignedTo
                              ? 'border-red-300 focus:ring-red-400'
                              : 'border-gray-300'
                          }`}
                          displayValue={(userId: string) => {
                            const user = (groupMembersData || []).find((u: any) => u.id === userId);
                            return user ? `${user.firstName} ${user.lastName}` : '';
                          }}
                          onChange={(event) => setAssignedToQuery(event.target.value)}
                          placeholder={!formData.assignmentGroupId ? 'Select Assignment Group first' : 'Search assignees...'}
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
                        </Combobox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                          afterLeave={() => setAssignedToQuery('')}
                        >
                          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {filteredGroupMembers.length === 0 ? (
                              <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                No members found.
                              </div>
                            ) : (
                              filteredGroupMembers.map((user: any) => (
                                <Combobox.Option
                                  key={user.id}
                                  value={user.id}
                                  className={({ active }) =>
                                    `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                      active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                    }`
                                  }
                                >
                                  {({ active, selected }) => (
                                    <>
                                      <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                        {user.firstName} {user.lastName}
                                      </span>
                                      {selected && (
                                        <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </Combobox.Option>
                              ))
                            )}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                    {errors.assignedTo && (
                      <p className="mt-1 text-sm text-red-600">{errors.assignedTo}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 8: Short Description (full width) */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-12">
                <div className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-2">
                    <label htmlFor="shortDescription" className="block text-sm font-medium text-gray-700 mb-1">
                      Short Description <span className="text-red-500">*</span>
                    </label>
                  </div>
                  <div className="col-span-10">
                    <RichTextEditor
                      value={formData.shortDescription}
                      onChange={(value) => {
                        setFormData(prev => ({ ...prev, shortDescription: value }));
                        if (errors.shortDescription) {
                          setErrors(prev => ({ ...prev, shortDescription: '' }));
                        }
                      }}
                      placeholder="Brief summary of the service request"
                      minHeight="120px"
                      className={errors.shortDescription ? 'border-red-300' : ''}
                    />
                    {errors.shortDescription && (
                      <p className="mt-1 text-sm text-red-600">{errors.shortDescription}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Row 9: Description (full width) */}
            <div className="grid grid-cols-12 gap-6 mb-6">
              <div className="col-span-12">
                <div className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-2">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                  </div>
                  <div className="col-span-10">
                    <RichTextEditor
                      value={formData.description}
                      onChange={(value) => {
                        setFormData(prev => ({ ...prev, description: value }));
                      }}
                      placeholder="Detailed description of the service request"
                      minHeight="180px"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/service-requests')}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createServiceRequestMutation.isPending}
                className="inline-flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {createServiceRequestMutation.isPending ? 'Creating...' : 'Create Service Request'}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default ServiceRequestCreate;